import z, { CustomErrorParams } from 'zod'

export const TRADITIONAL = 'traditional'
export const HSA = 'HSA'

const isNumber = <T>(data: T) => !isNaN(+data)

const optionalNumber = () =>
  z
    .string()
    .nullable()
    .transform((data) => {
      if (data === '') {
        return null
      }
      return data
    })
    .refine((data) => {
      if (data === null) {
        return true
      }
      return isNumber(data)
    }, 'Must be a number or blank')

const requiredNumber = () =>
  z.string().min(1, 'Required').refine(isNumber, 'Must be a number')

const rangeNumberSchema = (
  min: number,
  max: number,
  increment = 10,
  unit = '$',
  unitAfter = false
) => {
  let errorText
  if (unitAfter) {
    errorText = `Please input a desired value from ${min}${unit}-${max}${unit}`
  } else {
    errorText = `Please input a desired value from ${unit}${min}-${unit}${max}`
  }
  return z
    .number({
      coerce: true
    })
    .min(min, errorText)
    .max(max, errorText)
    .int()
    .multipleOf(increment, `Must be a multiple of ${increment}`)
}

const isSetNonPartial = <T extends Object>(obj: T) => {
  let isEmpty: boolean | null = null
  return Object.values(obj).every((val) => {
    const isCurrentValEmpty = !val || val === ''
    if (isEmpty === null) {
      isEmpty = isCurrentValEmpty
      return true
    }
    if (isEmpty !== isCurrentValEmpty) {
      return false
    }
    return true
  })
}

const refineNoPartialSet = (): [typeof isSetNonPartial, CustomErrorParams] => [
  isSetNonPartial,
  {
    path: ['employeeOnly'],
    message:
      'You must enter all tiers or leave them all blank. Partial row is not allowed.'
  }
]

const baseSchema = z.object({
  id: z.number().optional(),
  planName: z
    .string()
    .min(1, 'Plan name is required')
    .refine(
      (val) => /^[A-Za-z0-9 ]*$/.test(val),
      'Only letters and numbers are allowed'
    ),
  memberCoinsurance: requiredNumber().refine((val) => {
    return +val <= 30 && +val >= 10
  }, 'Percentage must be between 10 and 30'),
  enrollmentCount: z
    .object({
      employeeOnly: optionalNumber(),
      employeePlusSpouse: optionalNumber(),
      employeePlusChildren: optionalNumber(),
      employeePlusFamily: optionalNumber()
    })
    .refine(...refineNoPartialSet()),
  rates: z
    .object({
      employeeOnly: optionalNumber().transform((d) => d?.toString() ?? null),
      employeePlusSpouse: optionalNumber().transform(
        (d) => d?.toString() ?? null
      ),
      employeePlusChildren: optionalNumber().transform(
        (d) => d?.toString() ?? null
      ),
      employeePlusFamily: optionalNumber().transform(
        (d) => d?.toString() ?? null
      )
    })
    .refine(...refineNoPartialSet()),
  renewalRates: z
    .object({
      employeeOnly: optionalNumber().transform((d) => d?.toString() ?? null),
      employeePlusSpouse: optionalNumber().transform(
        (d) => d?.toString() ?? null
      ),
      employeePlusChildren: optionalNumber().transform(
        (d) => d?.toString() ?? null
      ),
      employeePlusFamily: optionalNumber().transform(
        (d) => d?.toString() ?? null
      )
    })
    .refine(...refineNoPartialSet())
})

export const previousPlanSchema = z
  .discriminatedUnion(
    'type',
    [
      z
        .object({
          type: z.literal(HSA),
          indInnDeductible: rangeNumberSchema(1600, 8050),
          indInnOOP: rangeNumberSchema(1600, 8050)
        })
        .merge(baseSchema),
      z
        .object({
          type: z.literal(TRADITIONAL),
          indInnDeductible: rangeNumberSchema(0, 9100),
          indInnOOP: rangeNumberSchema(0, 9100)
        })
        .merge(baseSchema)
    ],
    {
      errorMap: (issue) => {
        switch (issue.code) {
          case 'invalid_union_discriminator':
            return { message: 'A type is required' }
          default:
            return { message: 'Type is invalid' }
        }
      }
    }
  )
  .refine((schema) => schema.indInnDeductible <= schema.indInnOOP, {
    message: 'OOP Max cannot be less than deductible',
    path: ['indInnOOP']
  })

export type IPreviousPlanForm = z.infer<typeof previousPlanSchema>
